/**
 * "Painel da chamada" (esboço 3.3): a coluna ao lado do vídeo, só na sala ampliada. Tem abas: a "Transcrição" e, para o
 * atendente, "Participantes" (quem espera para entrar e quem está na chamada; as abas Jev, Notas e Ferramentas do esboço
 * chegam com as próximas funções da Jev). Fecha no X e reabre pelo botão da barra.
 *
 * Com uma aba só (o padrão), os filhos são o conteúdo dela. Com várias (`ownPanels`), quem monta o painel entrega cada aba
 * como um `role="tabpanel"` (`id="call-panel-<id>"`) nos filhos: assim a Transcrição pode ficar montada, só escondida, e
 * não perde a rolagem ao trocar de aba.
 */
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DOCK_PANEL_HEIGHT } from './popoutWindow';

function CloseButton({ onClose }) {
    return (
        <button
            type="button"
            aria-label="Fechar o painel da chamada"
            onClick={onClose}
            className="rounded-full p-1.5 text-neutral-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
        >
            <X className="h-4 w-4" aria-hidden="true" />
        </button>
    );
}

/** A aba que existe sempre (e a única quando o painel não recebe `tabs`). */
export const TRANSCRIPT_TAB = Object.freeze({ id: 'transcript', label: 'Transcrição' });

/** O id do painel de uma aba (o `aria-controls` dela). */
export const panelIdOf = (tabId) => `call-panel-${tabId}`;
export const tabIdOf = (tabId) => `call-panel-tab-${tabId}`;

function Tabs({ tabs, activeId, onSelect, className }) {
    // Setas: a navegação de abas do teclado (cada aba só entra na ordem do Tab quando é a selecionada)
    const move = (event, index) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const next = tabs[(index + step + tabs.length) % tabs.length];
        onSelect?.(next.id);
        event.currentTarget.parentElement?.querySelector(`#${tabIdOf(next.id)}`)?.focus();
    };
    return (
        <div role="tablist" aria-label="Abas do painel" className={cn('flex gap-1', className)}>
            {tabs.map((tab, index) => {
                const selected = tab.id === activeId;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        id={tabIdOf(tab.id)}
                        aria-selected={selected}
                        aria-controls={panelIdOf(tab.id)}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onSelect?.(tab.id)}
                        onKeyDown={(event) => move(event, index)}
                        className={cn(
                            'flex items-center border-b-2 px-3 py-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300',
                            selected ? 'border-cyan-300 text-cyan-200' : 'border-transparent text-neutral-300 hover:text-white',
                        )}
                    >
                        {tab.label}
                        {tab.badge > 0 && (
                            <span
                                data-tab-badge=""
                                aria-label={`${tab.badge} aguardando`}
                                className="ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-cyan-400 px-1 text-[11px] font-bold leading-[18px] text-black"
                            >
                                {tab.badge}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * @param {{ onClose: () => void, layout?: 'side'|'sheet'|'dock', tabs?: Array<{ id: string, label: string, badge?: number }>,
 *   activeTab?: string, onTabChange?: (id: string) => void, ownPanels?: boolean, children: React.ReactNode }} props
 *   `tabs`/`activeTab`/`onTabChange`: as abas (só a Transcrição, se não vierem) e qual está aberta; `badge` é a contagem no
 *   selo da aba (quem espera para entrar). `ownPanels`: os filhos já são os `tabpanel` de cada aba.
 *   `layout`: `side` é a coluna de 380 px ao lado do vídeo; `sheet` ocupa a largura toda e divide a altura com o vídeo,
 *   para a tela estreita (entre o vídeo e a barra de controles); `dock` é a gaveta de altura fixa (`DOCK_PANEL_HEIGHT`)
 *   das janelas compactas (a janelinha da guia e a flutuante), que crescem para acomodá-la (no máximo 60% da janela,
 *   se o navegador não a deixar crescer). Em `sheet` e `dock` há
 *   pouca altura: o título some (continua no nome acessível) e a aba e o X dividem uma linha só.
 */
export function CallPanel({ onClose, layout = 'side', tabs = [TRANSCRIPT_TAB], activeTab = tabs[0].id, onTabChange, ownPanels = false, children }) {
    const sheet = layout !== 'side';
    return (
        <aside
            aria-label="Painel da chamada"
            data-call-panel=""
            data-layout={layout}
            className={cn(
                'flex min-h-0 flex-col overflow-hidden border border-white/10 bg-neutral-900 text-neutral-50',
                layout === 'side' ? 'w-[380px] shrink-0 rounded-2xl' : 'w-full rounded-t-2xl border-b-0',
                layout === 'sheet' && 'flex-[2_1_0%]',
                layout === 'dock' && 'shrink-0',
            )}
            // Altura fixa: é exatamente o que a janela cresce para acomodar a gaveta (`DOCK_PANEL_HEIGHT`). O teto de 60%
            // vale quando o navegador não deixa a janela crescer o bastante: o vídeo cede, mas não some.
            style={layout === 'dock' ? { height: DOCK_PANEL_HEIGHT, maxHeight: '60%' } : undefined}
        >
            {sheet ? (
                <div className="flex items-center justify-between border-b border-white/10 pl-2 pr-2">
                    <Tabs tabs={tabs} activeId={activeTab} onSelect={onTabChange} />
                    <CloseButton onClose={onClose} />
                </div>
            ) : (
                <>
                    <header className="flex items-center justify-between px-4 pb-1 pt-3.5">
                        <h2 className="text-base font-semibold">Painel da chamada</h2>
                        <CloseButton onClose={onClose} />
                    </header>
                    <Tabs tabs={tabs} activeId={activeTab} onSelect={onTabChange} className="border-b border-white/10 px-3" />
                </>
            )}
            {ownPanels ? children : (
                <div role="tabpanel" id={panelIdOf(activeTab)} aria-labelledby={tabIdOf(activeTab)} className="flex min-h-0 flex-1 flex-col">
                    {children}
                </div>
            )}
        </aside>
    );
}
